import React from "react";
import { useAppSelector } from "../../../store/hooks";
import { calculateModuleCost } from "../../../utils/validation/moduleValidation";
import { ModuleSaveLoadButtons } from "../../ModuleSaveLoadButtons";
import { ModuleResetButton } from "../../ModuleResetButton";
import { 
  ModuleSpecifications
} from "../../../types/moduleBuilder";

/**
 * Module Export Step Component
 * Handles displaying final module statistics and builder selection
 */
export const ModuleExportStep: React.FC = () => {
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };

  const generateModuleSpecifications = (): ModuleSpecifications => {
    const baseModuleCost = calculateModuleCost(moduleConfig.size);
    
    // Collect all systems from all categories (like ModuleCalculationsStep does)
    const allSystems: any[] = [];
    
    // Core systems
    if (moduleConfig.systems?.bridge) {
      allSystems.push({
        name: moduleConfig.systems.bridge.name || "Bridge",
        description: moduleConfig.systems.bridge.description || "",
        cost: moduleConfig.systems.bridge.cost || 0,
        tonnage: moduleConfig.systems.bridge.tonnage || 0,
        power: moduleConfig.systems.bridge.power || 0,
        techLevel: moduleConfig.systems.bridge.techLevel || moduleConfig.techLevel,
        type: "Bridge"
      });
    }
    
    if (moduleConfig.systems?.computer?.length) {
      moduleConfig.systems.computer.forEach((comp: any) => {
        allSystems.push({
          name: comp.name || "Computer",
          description: comp.description || "",
          cost: comp.cost || 0,
          tonnage: comp.tonnage || 0,
          power: comp.power || 0,
          techLevel: comp.techLevel || moduleConfig.techLevel,
          type: "Computer"
        });
      });
    }
    
    if (moduleConfig.systems?.sensors) {
      allSystems.push({
        name: moduleConfig.systems.sensors.name || "Sensors",
        description: moduleConfig.systems.sensors.description || "",
        cost: moduleConfig.systems.sensors.cost || 0,
        tonnage: moduleConfig.systems.sensors.tonnage || 0,
        power: moduleConfig.systems.sensors.power || 0,
        techLevel: moduleConfig.systems.sensors.techLevel || moduleConfig.techLevel,
        type: "Sensors"
      });
    }
    
    if (moduleConfig.systems?.powerPlant?.length) {
      moduleConfig.systems.powerPlant.forEach((plant: any) => {
        allSystems.push({
          name: plant.name || "Power Plant",
          description: plant.description || "",
          cost: plant.cost || 0,
          tonnage: plant.tonnage || 0,
          power: plant.power || 0,
          techLevel: plant.techLevel || moduleConfig.techLevel,
          type: "Power Plant"
        });
      });
    }
    
    if (moduleConfig.systems?.drives?.length) {
      moduleConfig.systems.drives.forEach((drive: any) => {
        allSystems.push({
          name: drive.name || "Drive",
          description: drive.description || "",
          cost: drive.cost || 0,
          tonnage: drive.tonnage || 0,
          power: drive.power || 0,
          techLevel: drive.techLevel || moduleConfig.techLevel,
          type: "Drive"
        });
      });
    }
    
    if (moduleConfig.systems?.fuel) {
      allSystems.push({
        name: moduleConfig.systems.fuel.name || "Fuel",
        description: moduleConfig.systems.fuel.description || "",
        cost: moduleConfig.systems.fuel.cost || 0,
        tonnage: moduleConfig.systems.fuel.tonnage || 0,
        power: moduleConfig.systems.fuel.power || 0,
        techLevel: moduleConfig.systems.fuel.techLevel || moduleConfig.techLevel,
        type: "Fuel"
      });
    }
    
    if (moduleConfig.systems?.staterooms?.length) {
      moduleConfig.systems.staterooms.forEach((stateroom: any) => {
        allSystems.push({
          name: stateroom.name || "Stateroom",
          description: stateroom.description || "",
          cost: stateroom.cost || 0,
          tonnage: stateroom.tonnage || 0,
          power: stateroom.power || 0,
          techLevel: stateroom.techLevel || moduleConfig.techLevel,
          type: "Stateroom"
        });
      });
    }
    
    if (moduleConfig.systems?.weapons?.length) {
      moduleConfig.systems.weapons.forEach((weapon: any) => {
        allSystems.push({
          name: weapon.name || "Weapon",
          description: weapon.description || "",
          cost: weapon.cost || 0,
          tonnage: weapon.tonnage || 0,
          power: weapon.power || 0,
          techLevel: weapon.techLevel || moduleConfig.techLevel,
          type: "Weapon"
        });
      });
    }
    
    // Optional systems
    if (moduleConfig.systems?.optionalSystems?.length) {
      moduleConfig.systems.optionalSystems.forEach((sys: any) => {
        allSystems.push({
          name: sys.name || "Optional System",
          description: sys.description || "",
          cost: sys.cost || 0,
          tonnage: sys.tonnage || 0,
          power: sys.power || 0,
          techLevel: sys.techLevel || moduleConfig.techLevel,
          type: "Optional System"
        });
      });
    }
    
    const systemsCost = allSystems.reduce((sum: number, sys: any) => sum + (sys.cost || 0), 0);
    const systemsTons = allSystems.reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
    const systemsPower = Math.round(allSystems.reduce((sum: number, sys: any) => sum + (sys.power || 0), 0) * 100) / 100;
    const totalCost = baseModuleCost + systemsCost;
    const remainingTons = moduleConfig.size - systemsTons;

    return {
      summary: {
        name: moduleConfig.name,
        size: moduleConfig.size,
        techLevel: moduleConfig.techLevel,
        totalCost,
        remainingTons,
        systemsCount: allSystems.length,
        totalPower: systemsPower,
      },
      costBreakdown: {
        baseModuleCost,
        systemsCost,
        totalCost,
        costPerTon: totalCost / moduleConfig.size,
      },
      tonnageBreakdown: {
        totalSize: moduleConfig.size,
        systemsTons,
        remainingTons,
        usagePercentage: ((moduleConfig.size - remainingTons) / moduleConfig.size) * 100,
      },
      systems: allSystems.map((sys: any) => ({
        name: sys.name,
        description: sys.description,
        cost: sys.cost,
        tons: sys.tonnage || sys.tons, // Handle both property names
        power: sys.power,
        techLevel: sys.techLevel,
        category: sys.type,
      })),
      travellerCompliance: {
        moduleCostPerTon: 25000, // Default cost per ton
        maxModularPercentage: 100,
        nonModularSystems: [],
        modularSystems: [],
      },
    };
  };

  const handlePrintSpecifications = () => {
    const specs = generateModuleSpecifications();
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${moduleConfig.name} - Module Specifications</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
              .section { margin-bottom: 30px; }
              .section h2 { color: #2563eb; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
              .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
              .spec-item { margin-bottom: 10px; }
              .spec-label { font-weight: bold; color: #374151; }
              .spec-value { color: #111827; }
              .systems-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
              .systems-table th, .systems-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .systems-table th { background-color: #f3f4f6; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${moduleConfig.name}</h1>
              <h2>Module Specifications</h2>
              <p>Generated on ${new Date().toLocaleString()}</p>
            </div>
            
            <div class="section">
              <h2>Module Summary</h2>
              <div class="grid">
                <div class="spec-item">
                  <span class="spec-label">Name:</span>
                  <span class="spec-value">${specs.summary.name}</span>
                </div>

                <div class="spec-item">
                  <span class="spec-label">Size:</span>
                  <span class="spec-value">${specs.summary.size} tons</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Tech Level:</span>
                  <span class="spec-value">${specs.summary.techLevel}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Total Cost:</span>
                  <span class="spec-value">${formatCurrency(specs.summary.totalCost)} Cr</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Remaining Tons:</span>
                  <span class="spec-value">${specs.summary.remainingTons} tons</span>
                </div>
              </div>
            </div>
            
            <div class="section">
              <h2>Cost Breakdown</h2>
              <div class="grid">
                <div class="spec-item">
                  <span class="spec-label">Base Module Cost:</span>
                  <span class="spec-value">${formatCurrency(specs.costBreakdown.baseModuleCost)} Cr</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Systems Cost:</span>
                  <span class="spec-value">${formatCurrency(specs.costBreakdown.systemsCost)} Cr</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Total Cost:</span>
                  <span class="spec-value">${formatCurrency(specs.costBreakdown.totalCost)} Cr</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Cost per Ton:</span>
                  <span class="spec-value">${formatCurrency(specs.costBreakdown.costPerTon)} Cr</span>
                </div>
              </div>
            </div>
            
            <div class="section">
              <h2>Installed Systems</h2>
              <table class="systems-table">
                <thead>
                  <tr>
                    <th>System</th>
                    <th>Description</th>
                    <th>Cost (Cr)</th>
                    <th>Tons</th>
                    <th>Power</th>
                    <th>TL</th>
                  </tr>
                </thead>
                <tbody>
                  ${specs.systems.map((sys) => `
                    <tr>
                      <td>${sys.name}</td>
                      <td>${sys.description}</td>
                      <td>${formatCurrency(sys.cost)}</td>
                      <td>${sys.tons}</td>
                      <td>${sys.power}</td>
                      <td>${sys.techLevel}</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
            </div>
            

          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="space-y-6">
      {/* Save/Load/Reset Actions */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">💾 Module Actions</h3>
        
        <div className="flex justify-end gap-2">
          <ModuleSaveLoadButtons variant="compact" />
          <ModuleResetButton variant="compact" />
        </div>
      </div>

      {/* Module Statistics */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">📊 Final Module Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {formatCurrency(calculateModuleCost(moduleConfig.size))}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Base Cost (Cr)</div>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(generateModuleSpecifications().costBreakdown.systemsCost)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Systems Cost (Cr)</div>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {generateModuleSpecifications().tonnageBreakdown.systemsTons.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Systems Tons</div>
          </div>
          <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/30 rounded">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {generateModuleSpecifications().summary.systemsCount}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Systems</div>
          </div>
        </div>
      </div>

      {/* Print Specifications */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">🖨️ Print Specifications</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Generate and print a detailed specification sheet for the module.
        </p>
        <button
          onClick={handlePrintSpecifications}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
        >
          Print Module Specifications
        </button>
      </div>
    </div>
  );
};